import {useEffect, useRef} from 'react'
import {tripService} from '@/services/tripService'
import {isDebug} from '@/utils/debug'

/**
 * BROADCAST THE PASSENGER'S POSITION TO THEIR DRIVER — the other half of `useMyLocation`,
 * kept deliberately separate from it.
 *
 * ── ⚠️ WHY IT IS A SECOND HOOK AND NOT A FLAG ON THE FIRST ────────────────────────
 * Reading your own location to centre your own map and sending it to another person are
 * two different acts, and only the first is implied by opening a map. Splitting them means
 * the disclosure has one caller, one gate and one place to look when someone asks what
 * this app does with a passenger's location — instead of being an option buried in a hook
 * that half the app calls.
 *
 * ── ⚠️ AND WHY THE DRIVER STILL MAY NOT SEE A PIN ─────────────────────────────────
 * Nothing here decides that. The driver's en-route map draws the pin only until the
 * pickup is departed, because after that the passenger's phone is travelling with the car
 * and the pin would sit on top of it. Sending past that point is harmless — dispatch reads
 * the same broadcast — so this does not try to mirror that rule.
 */

/**
 * HOW OFTEN — every two seconds, moving or not.
 *
 * ⚠️ NO DISTANCE CONDITION ANY MORE. It used to send only after a 5 m move, so a passenger
 * standing at the kerb sent ONE position and then nothing. Nothing on the backend replays
 * a last position, so a driver who opened the trip after that single send saw "waiting"
 * for a passenger who was sharing the whole time. A fixed heartbeat keeps every driver's
 * map current however late they look.
 */
const SEND_INTERVAL_MS = 2000

/**
 * @param {string|number|null} tripId  the ride whose driver should see the pin
 * @param {{lat: number, lng: number}|null} position  from `useMyLocation`
 * @param {boolean} enabled  the caller's gate — see `TrackDriver`
 */
/**
 * ONE TAG FOR THE WHOLE CHAIN, so a passenger's console and a driver's console can be read
 * side by side. The driver portal prints every raw socket frame under `[reverb]`; this is
 * the other end of the same story, and the gap between the two is where the fault is.
 *
 * ⚠️ ALWAYS ON, IN DEV AND IN A BUILD. The failure this exists to diagnose is silent by
 * construction — nothing throws when a position is never sent — and it happens on real
 * devices in real airports rather than on a laptop with the dev server running.
 */
const log = (...args) => {
    /* ⚠️ OFF BY DEFAULT, for the same reason as the map's. This answers "is the
       passenger sending", which was the question last week and is not this week's.
       `hopnShareDebug = true` in the console brings it back. */
    if (isDebug('hopnShareDebug')) console.log('%c[share]', 'color:#208AEF;font-weight:600', ...args)
}

export function useShareMyLocation(tripId, position, {enabled = true} = {}) {
    /* ⚠️ REFS, NOT STATE. Nothing on screen changes when a fix is sent, and putting the
       last-sent position in state would re-render the whole tracking page every few
       seconds — the same page that is running a map camera. */
    const inFlight = useRef(false)
    const sentOnce = useRef(false)

    /* ONE trip id or SEVERAL — the layout passes every ongoing trip, so each fix reaches
       every driver coming for this passenger. Keyed as a string so the effects below
       re-run when the SET changes, not whenever the caller builds a new array. */
    const tripIds = (Array.isArray(tripId) ? tripId : [tripId]).filter((id) => id != null && id !== '')
    const tripKey = tripIds.join(',')
    const tripIdsRef = useRef(tripIds)
    // Declared before the send effect below, so it has run by the time that one reads it.
    useEffect(() => { tripIdsRef.current = tripIds })

    const lat = position?.lat
    const lng = position?.lng

    /* ⚠️ THE GATES ARE LOGGED, NOT JUST THE SENDS. Every way this does nothing is
       invisible from the outside: the trip is in a phase that does not share, the browser
       has not given a position, the caller disabled it. All three look identical on the
       driver's map — no pin — and only this says which one it is. Logged on CHANGE, so a
       stationary passenger does not fill the console. */
    const gateRef = useRef(null)
    useEffect(() => {
        const gate = !enabled ? 'disabled by caller'
            : !tripKey ? 'no trip id'
            : !Number.isFinite(lat) || !Number.isFinite(lng) ? 'no position yet (permission, or no fix)'
            : 'sharing'
        if (gateRef.current === gate) return
        gateRef.current = gate
        log(`trip ${tripKey || '—'} · ${gate}`)
    }, [enabled, tripKey, lat, lng])

    /* The newest fix, read by the timer below — the timer is not rebuilt on every fix. */
    const positionRef = useRef(position)
    useEffect(() => { positionRef.current = position })

    const hasPosition = Number.isFinite(lat) && Number.isFinite(lng)

    useEffect(() => {
        if (!enabled || !tripKey || !hasPosition) return undefined

        const send = () => {
            const fix = positionRef.current
            if (!fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lng)) return
            // One request at a time. A slow network would otherwise stack fixes that are
            // stale by the time they land, and the newest one is the only one worth having.
            if (inFlight.current) return
            inFlight.current = true
            const here = {lat: fix.lat, lng: fix.lng}

            /* ⚠️ FAILURES ARE SWALLOWED ON PURPOSE. There is nothing a passenger could do
               about one and nothing to show them: the next send is two seconds away and
               carries the same information. */
            /* The whole fix, not just the point — accuracy, heading and speed ride along
               when the browser gave them. See `submitLocation` for what happens to each. */
            Promise.all(tripIdsRef.current.map((id) => tripService.submitLocation(id, fix)))
                .then((results) => {
                    const result = results.find((r) => !r?.success) || results[0]
                    /* Always printed — a failure, or the one-time "it works" line. A log of
                       every successful send is a console nobody can read. */
                    if (!result?.success) console.warn('%c[share]', 'color:#208AEF;font-weight:600', 'REJECTED', result?.statusCode ?? '', result?.error || '', here)
                    else if (!sentOnce.current) {
                        sentOnce.current = true
                        console.log('%c[share]', 'color:#208AEF;font-weight:600', `first position accepted · trips ${tripIdsRef.current.join(',')} · sending every ${SEND_INTERVAL_MS / 1000}s from here`, here)
                    }
                })
                .finally(() => { inFlight.current = false })
        }

        // The first position goes at once, then every SEND_INTERVAL_MS whether or not it moved.
        send()
        const timer = setInterval(send, SEND_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [enabled, tripKey, hasPosition])
}

export default useShareMyLocation
